import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import "filepond/dist/filepond.min.css";
import React, { useEffect, useRef, useState } from "react";
import { registerPlugin } from "react-filepond";
import { Controller, useForm } from "react-hook-form";
import { Label, Modal, ModalBody, ModalHeader } from "reactstrap";

import {
  ApproveButton,
  CancelButton,
  CloseButton,
  RejectButton,
  ReturnButton
} from "@/components/Common/Buttons";
import FileUpload from "@/components/Common/FileUpload";
import { AsyncSelect, InputField } from "@/components/Common/Inputs";
import { useNotificationModal } from "@/components/Hooks/useNotificationModal";
import { formatLoadOptions } from "@/helpers/data_helper";

import {
useApproveAccessRequestsMutation, useCancelAccessRequestsMutation,
useRejectAccessRequestsMutation, useReturnAccessRequestsMutation
} from "@/api/Endpoints/Core/Transactions/AccessRequest/AccessRequests";
import { useLookUpRolesMutation } from "@/api/Endpoints/Core/Security/Roles";

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

const MODULE_NAME = 'Access Request'

const ActionModal = ({
  accessRequestId,
  data = null,
  show,
  onCloseClick,
  customFunction,
}) => {
  const actionType = useRef("save");
  const [files, setFiles] = useState([]);
  const [lookUpRoles] = useLookUpRolesMutation();
  const { notification, NotificationContainer } = useNotificationModal();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      accessRequestId: accessRequestId || 0,
      remarks: "",
    },
  });

  useEffect(() => {
    if (accessRequestId !== 0) {
      reset({
        accessRequestId: accessRequestId || 0,
        remarks: "",
      });
    } else {
      reset({
        accessRequestId: 0,
        remarks: "",
      });
    }
  }, [accessRequestId, show]);

  // Calling Action API
  const [returnAction, { isLoading: isReturning }] = useReturnAccessRequestsMutation();
  const [approveAction, { isLoading: isApproving }] = useApproveAccessRequestsMutation();
  const [rejectAction, { isLoading: isRejecting }] = useRejectAccessRequestsMutation();
  const [cancelAction, { isLoading: isCanceling }] = useCancelAccessRequestsMutation();

  const renderMessage = (data) => {
    switch (data) {
      case "Approve": return "success";
      case "Return": return "info";
      case "Reject": return "danger";
      case "Cancel": return "danger";
      default: return "dark";
    }
  };

  const onSubmit = async (formData) => {
    const roleIds = (formData.roles || []).map((r) => ({ roleId: r.value || r.roleId, roleName: r.label || r.roleName }));
    const payload = {
      remarks: formData.remarks,
      accessRequestId: accessRequestId,
      roles: roleIds,
      files

    };

    try {
      if (actionType.current === "Approve") {
        const approved = await approveAction(payload);
        if (approved?.data?.success) {
          customFunction.updateState({ data: approved?.data?.returnData });
          notification({ type: 'success', header: `Approved ${MODULE_NAME}`, title: approved?.data?.returnData.referenceNo, message: 'Request was Approved' });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: 'error', title: MODULE_NAME, message: `${approved.data.returnMessage}` });
        }
      } else if (actionType.current === "Return") {
        const returned = await returnAction(payload);
        if (returned?.data?.success) {
          customFunction.updateState({ data: returned?.data?.returnData });
          notification({ type: 'reject', header: `Returned ${MODULE_NAME}`, title: returned?.data?.returnData.referenceNo, message: 'Request was Returned' });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: 'error', title: MODULE_NAME, message: `${returned.data.returnMessage}` });
        }
      } else if (actionType.current === "Reject") {
        const rejected = await rejectAction(payload);
        if (rejected?.data?.success) {
          customFunction.updateState({ data: rejected?.data?.returnData });
          notification({ type: 'reject', header: `Rejected ${MODULE_NAME}`, title: rejected?.data?.returnData.referenceNo, message: 'Request was Rejected' });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: 'error', title: MODULE_NAME, message: `${rejected.data.returnMessage}` });
        }
      } else if (actionType.current === "Cancel") {
        const canceled = await cancelAction(payload);
        if (canceled?.data?.success) {
          customFunction.updateState({ data: canceled?.data?.returnData });
          notification({ type: 'reject', header: `Canceled ${MODULE_NAME}`, title: canceled?.data?.returnData.referenceNo, message: 'Request was Canceled' });
          onCloseClick(true);
        } else {
          onCloseClick();
          notification({ type: 'error', title: MODULE_NAME, message: `${canceled.data.returnMessage}` });
        }
      }
    } catch (error) {
      notification({ type: 'error', title: MODULE_NAME, message: 'Something went wrong' });
    }
  };

  return (
    <React.Fragment>
      <NotificationContainer />
      <Modal modalClassName="flip" id="requestSave" size="lg" isOpen={show} toggle={() => onCloseClick(false)} centered>
        <ModalHeader toggle={() => onCloseClick(false)} className={`p-3 bg-${renderMessage(data?.actionName)}-subtle text-center w-100`}>
          <center>{`${data?.actionName} ${data?.referenceNo} `}</center>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3 pb-2">
              {data?.actionName === 'Approve' &&

                <div className='w-100'>
                  <AsyncSelect
                    name="roles"
                    control={control}
                    label="Roles"
                    loadOptions={formatLoadOptions(lookUpRoles)}
                    isMulti
                    rules={{
                      required: "Role is required.",
                    }}
                    defaultValue={[]}
                    placeholder="Select roles..."
                    getOptionValue={(opt) => opt.roleId || opt.value}
                    getOptionLabel={(opt) => opt.roleName || opt.label}

                  />
                </div>}
              <Label htmlFor="exampleFormControlTextarea" className="form-label">Remarks</Label>
              <Controller name="remarks" control={control} defaultValue="" render={({ field }) => (<InputField control={control} type="textarea" rows="4" maxLength={500} showCharCounter placeholder="Enter Remarks" {...field} />)} />
              <br />
              <FileUpload onFilesChange={setFiles} />
            </div>
            <div className="hstack gap-2 justify-content-end">
              <CloseButton onClick={onCloseClick} />
              {data?.actionName === "Return" ? (
                <ReturnButton isProcessing={isReturning} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Approve" ? (
                <ApproveButton isProcessing={isApproving} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Reject" ? (
                <RejectButton isProcessing={isRejecting} onClick={() => (actionType.current = data?.actionName)} />
              ) : data?.actionName === "Cancel" ? (
                <CancelButton isProcessing={isCanceling} onClick={() => (actionType.current = data?.actionName)} />
              ) : ''}
            </div>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ActionModal;
